import { BillingAddressType } from 'api/types';
import { UpdateUserAddressConfirmedRequestBody, UpdateUserAddressRequestBody } from 'api/user';
import { AxiosResponse } from 'axios';
import {
  perSessionAchCompletionTimeKey,
  perSessionAchValidateSuccessKey,
  perSessionAuthCopyAckModal,
  perSessionModalKey,
} from 'constants/localStorageKeys';
import useUser from 'hooks/useUser';
import AutopayModal, { AutoPayStep } from 'modules/status/components/AutoPayModal/AutopayModal';
import BillingAddressFormModal from 'modules/status/components/BillingAddressFormModal/BillingAddressFormModal';
import BillingAddressModal from 'modules/status/components/BillingAddressModal/BillingAddressModal';
import TpvModal from 'modules/status/components/TpvModal/TpvModal';
import { AchModalExitPoint, trackAchModalClosed } from 'modules/status/utils/trackAchModalEvent';
import React, { useEffect, useMemo } from 'react';
import { UseMutateFunction } from 'react-query';
import AuthCopyAckModal from 'shared/components/AuthCopyAckModal/AuthCopyAckModal';
import { FullStory } from '@fullstory/browser';
import { isWithinTPVBusinessHours } from '../../utils/tpvBusinessHours';
import { TpvUnavailableModal } from '../TpvUnavailableModal/TpvUnavailableModal';
import { MilestoneType } from '../../../types';

export enum EarlyAccessModalType {
  CHECKLIST,
  AUTOPAY,
  BILLING_ADDRESS,
  BILLING_FORM,
  TPV,
}

interface EarlyAccessModalProps {
  achModalEnabled: boolean;
  modalKey: any;
  isAchRedirect: boolean;
  hasAchBanner: boolean;
  openModal: EarlyAccessModalType | null;
  onSetOpenModal: (modal: EarlyAccessModalType | null) => void;
  showEntryPointInSystemDetails: boolean;
  onSetShowEntryPointInSystemDetails: (shouldShowEntryPoint: boolean) => void;
  addressDisplay: BillingAddressType | undefined;
  onSetAddressDisplay: (address: BillingAddressType | undefined) => void;
  onSetAchFormCompletionTime: (time: number) => void;
  onResetSearchParams: () => void;
  mutateBillingAddress: UseMutateFunction<
    AxiosResponse<any, any>,
    unknown,
    { user_id: string; body: UpdateUserAddressRequestBody },
    unknown
  >;
  mutateBillingAddressConfirmation: UseMutateFunction<
    AxiosResponse<any, any>,
    unknown,
    { user_id: string; confirmed: UpdateUserAddressConfirmedRequestBody },
    unknown
  >;
}

/** Handles displaying of / interactions with various Early Access stage modals */
export function EarlyAccessModals({
  modalKey,
  openModal,
  onSetOpenModal,
  addressDisplay,
  onSetAddressDisplay,
  onSetAchFormCompletionTime,
  onResetSearchParams,
  onSetShowEntryPointInSystemDetails,
  showEntryPointInSystemDetails,
  achModalEnabled,
  isAchRedirect,
  hasAchBanner,
  mutateBillingAddress,
  mutateBillingAddressConfirmation,
}: EarlyAccessModalProps) {
  const { userResult, selectedAgreement, refetch: refetchUser } = useUser();
  const user = userResult?.user;
  const changeBillingAddress = user?.address;

  const handleCloseModal = () => {
    const perSessionAchValidateSuccessRaw = localStorage.getItem(perSessionAchValidateSuccessKey);
    if (perSessionAchValidateSuccessRaw !== null) {
      const achValidateSuccessObj = JSON.parse(perSessionAchValidateSuccessRaw);
      if (
        selectedAgreement &&
        selectedAgreement.id in achValidateSuccessObj &&
        achValidateSuccessObj[selectedAgreement.id]
      ) {
        delete achValidateSuccessObj[selectedAgreement.id];
        localStorage.setItem(
          perSessionAchValidateSuccessKey,
          JSON.stringify(achValidateSuccessObj),
        );
        refetchUser();
      }
    }

    onSetOpenModal(null);
  };

  const isAchEnrolled = selectedAgreement?.ach_elected && selectedAgreement?.ach_verified;

  useEffect(() => {
    if (isAchRedirect) onSetOpenModal(EarlyAccessModalType.AUTOPAY);
  }, [isAchRedirect, onSetOpenModal]);

  useEffect(() => {
    if (openModal !== null) {
      FullStory('trackEvent', {
        name: 'Early Access Modal - Viewed',
        properties: {
          modalType: EarlyAccessModalType[openModal],
        },
      });
    }
  }, [openModal]);

  const initialAutoPayStep = useMemo(() => {
    if (isAchRedirect && isAchEnrolled) {
      return AutoPayStep.ALREADY_ENROLLED;
    }

    // skip terms and conditions if user attempted to enroll previously but their info hasn't yet been successfully verified
    if (
      (selectedAgreement?.ach_elected && !selectedAgreement?.ach_verified) ||
      (selectedAgreement?.ach_verified &&
        selectedAgreement.current_milestone !== MilestoneType.IN_SERVICE)
    )
      return AutoPayStep.ADD_BANK_ACCOUNT;

    // surface terms and conditions if the user hasn't enrolled elsewhere already
    if (!selectedAgreement?.ach_elected) return AutoPayStep.TERM_CONDITION;

    return AutoPayStep.SETUP_ACH;
  }, [isAchRedirect, isAchEnrolled, selectedAgreement]);

  const showAutopayModal = achModalEnabled && !!selectedAgreement && hasAchBanner;

  const handleCloseAutoPayModal = () => {
    handleCloseModal();
    onSetShowEntryPointInSystemDetails(false);
    // clear the search params if we're closing the modal from the direct-access route
    if (isAchRedirect) onResetSearchParams();
  };

  const handleOpenBillingForm = () => {
    onSetAddressDisplay(undefined);
    onSetOpenModal(EarlyAccessModalType.BILLING_FORM);
  };

  const handleCloseBillingAddressModal = () => {
    handleCloseModal();
    onSetAddressDisplay(undefined);
  };

  const handleBackBillingForm = async () => {
    onSetOpenModal(EarlyAccessModalType.BILLING_ADDRESS);
  };

  const handleSubmitBillingForm = (formdata: BillingAddressType) => {
    onSetOpenModal(EarlyAccessModalType.BILLING_ADDRESS);
    onSetAddressDisplay(formdata);
  };

  const handleConfirmBillingAddress = () => {
    handleCloseModal();
    const street1 = addressDisplay?.street_1;
    const street2 = addressDisplay?.street_2;
    const city = addressDisplay?.city;
    const state = addressDisplay?.state;
    const zipCode = addressDisplay?.zip_code;
    const confirmed = true;
    if (changeBillingAddress) {
      if (addressDisplay !== undefined) {
        mutateBillingAddress({
          user_id: user?.id,
          body: {
            address: {
              street_1: street1 || '',
              street_2: street2 || '',
              city: city || '',
              state: state || '',
              zip_code: zipCode || '',
              confirmed,
            },
          },
        });
      } else {
        mutateBillingAddressConfirmation({
          user_id: user?.id,
          confirmed: { address: { confirmed } },
        });
      }
    }
  };

  const handleCompleteAutopayModal = () => {
    if (!selectedAgreement?.id) return;

    const perSessionModalsRaw = localStorage.getItem(perSessionModalKey);
    if (perSessionModalsRaw !== null && modalKey) {
      const modalsObj = JSON.parse(perSessionModalsRaw);
      modalsObj[modalKey] = true;
      localStorage.setItem(perSessionModalKey, JSON.stringify(modalsObj));
    } else if (modalKey) {
      localStorage.setItem(perSessionModalKey, JSON.stringify({ [modalKey]: true }));
    }

    const perSessionAchCompletionTimeRaw = localStorage.getItem(perSessionAchCompletionTimeKey);
    if (perSessionAchCompletionTimeRaw !== null) {
      const completionTimeObj = JSON.parse(perSessionAchCompletionTimeRaw);
      completionTimeObj[selectedAgreement.id] = Date.now();
      localStorage.setItem(perSessionAchCompletionTimeKey, JSON.stringify(completionTimeObj));
    } else {
      localStorage.setItem(
        perSessionAchCompletionTimeKey,
        JSON.stringify({ [selectedAgreement.id]: Date.now() }),
      );
    }

    onSetAchFormCompletionTime(Date.now());
    trackAchModalClosed(AchModalExitPoint.GOT_IT_BUTTON);
  };

  return (
    <>
      <AuthCopyAckModal modalKey={perSessionAuthCopyAckModal} />
      {showAutopayModal && (
        <AutopayModal
          isOpen={openModal === EarlyAccessModalType.AUTOPAY}
          modalKey={modalKey}
          agreement={selectedAgreement}
          onCompleteFormModal={handleCompleteAutopayModal}
          onCloseAutopayModal={handleCloseAutoPayModal}
          showEntryPointInSystemDetails={showEntryPointInSystemDetails}
          initialStep={initialAutoPayStep}
        />
      )}
      {selectedAgreement && selectedAgreement.is_tpv_installer && (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <>
          {isWithinTPVBusinessHours() ? (
            <TpvModal
              agreementId={selectedAgreement.id}
              isOpen={openModal === EarlyAccessModalType.TPV}
              onCloseModal={handleCloseModal}
              onStartCallClickComplete={refetchUser}
            />
          ) : (
            <TpvUnavailableModal
              isOpen={openModal === EarlyAccessModalType.TPV}
              onCloseModal={handleCloseModal}
            />
          )}
        </>
      )}
      <BillingAddressModal
        isOpen={openModal === EarlyAccessModalType.BILLING_ADDRESS}
        addressDisplay={addressDisplay}
        onChangeConfirmed={handleConfirmBillingAddress}
        onOpenBillingForm={handleOpenBillingForm}
        onCloseBillingAddressModal={handleCloseBillingAddressModal}
        addressToChange={changeBillingAddress}
      />
      <BillingAddressFormModal
        openBillingFormModal={openModal === EarlyAccessModalType.BILLING_FORM}
        handleCloseBillingForm={handleCloseModal}
        handleBackBillingForm={handleBackBillingForm}
        handleSubmitBillingForm={handleSubmitBillingForm}
      />
    </>
  );
}

export default EarlyAccessModals;
